import React, { Component, useState, useEffect, memo } from "react";
import { Badge, Input, CustomInput } from "reactstrap";
import ReactTable from "react-table";
import Pagination from "../../../components/DatatablePagination";
import moment from "moment";
import { AddCommaNumber, AddCommaPhoneNumber } from "../../../helpers/Utils";
import { injectIntl } from "react-intl";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";

// --- Tier Color Config ---
const TIER_COLORS = {
  bronze: { bg: "#ffedd5", text: "#c2410c" },
  silver: { bg: "#eef2f7", text: "#475569" },
  gold: { bg: "#fff4d6", text: "#b7791f" },
  diamond: { bg: "#e0f7ff", text: "#0369a1" },
};

const getTierStyle = (armorial) => {
  if (!armorial) return {};
  const key = armorial.toLowerCase();
  for (const tier in TIER_COLORS) {
    if (key.includes(tier) || key === tier) return TIER_COLORS[tier];
  }
  if (key.includes("đồng")) return TIER_COLORS.bronze;
  if (key.includes("bạc")) return TIER_COLORS.silver;
  if (key.includes("vàng")) return TIER_COLORS.gold;
  if (key.includes("kim cương") || key.includes("kim cuong"))
    return TIER_COLORS.diamond;
  return {};
};

// --- Editable Cell Components ---
const EditableCell = memo(({ value, onChange, placeholder = "" }) => {
  const [localValue, setLocalValue] = useState(value || "");

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const handleBlur = () => {
    if (localValue !== value) onChange(localValue);
  };

  return (
    <Input
      type="text"
      bsSize="sm"
      value={localValue}
      placeholder={placeholder}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={handleBlur}
    />
  );
});

const NumberEditableCell = memo(({ value, onChange, placeholder = "0" }) => {
  const [displayValue, setDisplayValue] = useState(
    value ? AddCommaNumber(value) : "",
  );

  useEffect(() => {
    setDisplayValue(value ? AddCommaNumber(value) : "");
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/\./g, "");
    if (raw === "" || /^\d+$/.test(raw)) {
      setDisplayValue(raw ? AddCommaNumber(raw) : "");
    }
  };

  const handleBlur = () => {
    const raw = displayValue.replace(/\./g, "");
    if (raw !== String(value || "").replace(/\./g, "")) {
      onChange(raw);
    }
  };

  return (
    <Input
      type="text"
      bsSize="sm"
      value={displayValue}
      placeholder={placeholder}
      style={{ textAlign: "right" }}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
});

// --- Main Component ---
class ContentMultiForm extends Component {
  constructor(props) {
    super(props);
  }

  handleCellChange = (index, field, value) => {
    const { data, onUpdateData } = this.props;
    if (!data || !data[index]) return;

    const updatedItem = { ...data[index], [field]: value };
    if (onUpdateData) onUpdateData([updatedItem]);
  };

  getColumns = () => {
    const { data_tags, intl, selectedItems, toggleItem, onSelectAll, data } =
      this.props;

    // Map customer_id → tags
    const tagsByCustomer = {};
    if (data_tags && data_tags.length) {
      data_tags.forEach((tag) => {
        if (!tag.customer_id) return;
        if (!tagsByCustomer[tag.customer_id])
          tagsByCustomer[tag.customer_id] = [];
        tagsByCustomer[tag.customer_id].push(tag);
      });
    }

    return [
      {
        Header: (props) => {
          return (
            <div className="d-flex justify-content-center w-100">
              <CustomInput
                type="checkbox"
                id="checkAll"
                label=""
                className="cursor-pointer"
                style={{ cursor: "pointer" }}
                checked={
                  selectedItems &&
                  selectedItems.length > 0 &&
                  selectedItems.length >= data.length
                }
                onChange={() => onSelectAll(true)}
              />
            </div>
          );
        },
        accessor: "id",
        width: 50,
        sortable: false,
        Cell: (cellProps) => {
          return (
            <div className="d-flex justify-content-center w-100">
              <CustomInput
                type="checkbox"
                id={`check_${cellProps.value}`}
                label=""
                className="cursor-pointer"
                style={{ cursor: "pointer" }}
                checked={
                  selectedItems && selectedItems.includes(cellProps.value)
                }
                onChange={() => toggleItem(cellProps.value)}
              />
            </div>
          );
        },
      },
      {
        Header: intl.formatMessage({ id: "customer.header.name" }),
        accessor: "name",
        minWidth: 160,
        Cell: (props) => (
          <EditableCell
            value={props.value}
            onChange={(val) => this.handleCellChange(props.index, "name", val)}
          />
        ),
      },
      {
        Header: intl.formatMessage({ id: "customer.header.phone" }),
        accessor: "phone",
        minWidth: 120,
        Cell: (props) => (
          <EditableCell
            value={props.value}
            onChange={(val) => this.handleCellChange(props.index, "phone", val)}
          />
        ),
      },
      {
        Header: intl.formatMessage({ id: "customer.header.email" }),
        accessor: "email",
        minWidth: 140,
        Cell: (props) => (
          <EditableCell
            value={props.value}
            placeholder="email"
            onChange={(val) => this.handleCellChange(props.index, "email", val)}
          />
        ),
      },
      {
        Header: intl.formatMessage({ id: "customer.header.address" }),
        accessor: "address",
        minWidth: 150,
        Cell: (props) => (
          <EditableCell
            value={props.value}
            placeholder=""
            onChange={(val) =>
              this.handleCellChange(props.index, "address", val)
            }
          />
        ),
      },
      {
        Header: "Tags",
        accessor: "id",
        id: "tags",
        minWidth: 130,
        Cell: (cellProps) => {
          const tags = tagsByCustomer[cellProps.value] || [];
          if (!tags.length) return <span className="text-muted">—</span>;
          return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              {tags.map((tag) => (
                <Badge
                  key={tag.value}
                  color=""
                  style={{
                    backgroundColor: "#e9f8ef",
                    color: "#198754",
                    fontSize: "11px",
                    fontWeight: 800,
                    padding: "2px 8px",
                    borderRadius: "6px",
                  }}
                >
                  {tag.label}
                </Badge>
              ))}
            </div>
          );
        },
        sortable: false,
        filterable: false,
      },
      {
        Header: intl.formatMessage({ id: "customer.header.points" }),
        accessor: "point",
        width: 100,
        Cell: (props) => (
          <NumberEditableCell
            value={props.value}
            onChange={(val) => this.handleCellChange(props.index, "point", val)}
          />
        ),
      },
      {
        Header: intl.formatMessage({ id: "customer.header.used_count" }),
        accessor: "used",
        width: 70,
        Cell: (props) => (
          <div className="d-flex align-items-center h-100 justify-content-center">
            {AddCommaNumber(props.value)}
          </div>
        ),
      },
      {
        Header: intl.formatMessage({ id: "customer.header.total_spent" }),
        accessor: "total_spent",
        minWidth: 110,
        Cell: (props) => (
          <div className="d-flex align-items-center h-100 justify-content-end">
            {AddCommaNumber(props.value || 0)}
          </div>
        ),
      },
      {
        Header: intl.formatMessage({ id: "customer.header.rank" }),
        accessor: "armorial",
        width: 100,
        Cell: (cellProps) => {
          const tierStyle = getTierStyle(cellProps.value);
          if (tierStyle.bg) {
            return (
              <Badge
                color=""
                style={{
                  backgroundColor: tierStyle.bg,
                  color: tierStyle.text,
                  fontSize: "12px",
                  fontWeight: 800,
                  padding: "3px 9px",
                  borderRadius: "6px",
                  letterSpacing: 0,
                }}
              >
                {cellProps.value}
              </Badge>
            );
          }
          return <span>{cellProps.value}</span>;
        },
      },
      {
        Header: intl.formatMessage({ id: "customer.header.debt" }),
        accessor: "debt",
        minWidth: 100,
        Cell: (props) => (
          <NumberEditableCell
            value={props.value}
            onChange={(val) => this.handleCellChange(props.index, "debt", val)}
          />
        ),
      },
      {
        Header: intl.formatMessage({ id: "customer.header.created_at" }),
        accessor: "createdAt",
        width: 110,
        Cell: (props) => (
          <div className="d-flex align-items-center h-100">
            {moment(props.value).format("DD/MM/YYYY")}
          </div>
        ),
      },
      {
        Header: intl.formatMessage({ id: "customer.header.updated_at" }),
        accessor: "updatedAt",
        width: 110,
        Cell: (props) => (
          <div className="d-flex align-items-center h-100">
            {moment(props.value).format("DD/MM/YYYY")}
          </div>
        ),
      },
    ];
  };

  render() {
    const { data } = this.props;

    return (
      <div className="customer-multi-form-container">
        <PerfectScrollbar
          options={{ suppressScrollX: false, suppressScrollY: false }}
          style={{ width: "100%", maxHeight: "70vh" }}
        >
          <div style={{ minWidth: "100%", width: "max-content" }}>
            <ReactTable
              data={data || []}
              columns={this.getColumns()}
              defaultPageSize={20}
              showPagination={true}
              PaginationComponent={Pagination}
              minRows={1}
              NoDataComponent={() => null}
              className="-highlight customer-multi-form-table"
            />
          </div>
        </PerfectScrollbar>
      </div>
    );
  }
}

export default injectIntl(ContentMultiForm);
